import { useRouter } from "next/router"
import { useCallback, useEffect, useRef } from "react"
import { observer } from "mobx-react"
import {
  Contacts,
  LegalNavigation,
  SpecProjectsNav,
  useStore,
} from "@features/common"
import classNames from "classnames"
import { RouterLink } from "@lib/routing"
import { ButtonContained } from "@ui"
import { IconMenuClose } from "@lib/icons"

import type { ReactElement } from "react"

type MainNavigationProps = {
  opened?: boolean
  legal?: boolean
  children?: ReactElement | null
  enterLink?: string | null
}

export const MainNavigation = observer(function MainNavigation(
  props: MainNavigationProps,
) {
  const { GlobalModel, BlogModel } = useStore()
  const router = useRouter()
  const isCustomChildren = Boolean(props.children)
  const { children = <LegalNavigation />, enterLink } = props

  const menuRef = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback(
    (event) => {
      if (GlobalModel.isMenuOpened) {
        if (
          !menuRef.current ||
          menuRef.current.contains(event.target as Node)
        ) {
          return
        }

        GlobalModel.closeMenu()
      }
    },
    [GlobalModel],
  )

  useEffect(() => {
    document.addEventListener("click", handleClickOutside)
    return function cleanup() {
      return document.removeEventListener("click", handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <div
      ref={menuRef}
      className={classNames(
        "fixed z-10 top-0 right-0 bottom-0 w-full flex flex-col bg-white animate-from-right tablet:max-h-full tablet:w-[428px]",
      )}
      data-test-id="navigation"
    >
      {GlobalModel.isMenuOpened && (
        <button
          className="absolute right-8 top-8 transition-opacity hover:opacity-80"
          onClick={GlobalModel.closeMenu}
          data-test-id="navigation__close_button"
        >
          <IconMenuClose className="text-black" />
        </button>
      )}
      <div className="pb-8 pt-13.5 pl-6 pr-23 scrollbar-width-none overflow-y-scroll tablet:pl-10 tablet:pr-32 tablet:pb-12">
        {isCustomChildren && <SpecProjectsNav />}
        {children}
      </div>
      <div
        className={classNames(
          "relative pt-6 pb-3.5 px-6 mt-auto border-t border-gray-50 shadow-[0_-4px_54px_rgba(0,0,0,0.15)]",
          "tablet:px-10 tablet:pt-8 tablet:pb-12 tablet:mt-auto",
        )}
      >
        <RouterLink
          key="/contact-us"
          uri="/contact-us"
          availableInChinese
          className={classNames(
            "text-lg tablet:text-22 tablet-m:leading-6 transition-colors hover:text-pink-300",
            router.pathname.includes("contact-us") && "text-pink-300",
          )}
          onClick={GlobalModel.closeMenu}
          data-test-id="navigation__contact_us"
        >
          {GlobalModel.getTemplate("menu.contactUs")}
        </RouterLink>
        <Contacts
          classes={{
            container: "mt-4 mb-8 tablet:mt-7 tablet:mb-12.5",
            link: "hover:text-pink-300",
          }}
        />
        <ButtonContained
          as="a"
          href={
            enterLink ||
            BlogModel.branch_links.sidebar_button ||
            GlobalModel.branch_links.sidebar_button ||
            BlogModel.BANNER_LINK
          }
          data-test-id="navigation__enter_pure"
          className="w-full tablet:max-w-[228px]"
        >
          {GlobalModel.getTemplate("menu.enter.pure")}
        </ButtonContained>
      </div>
    </div>
  )
})
